import { useState, useEffect } from "react";
import axios from "axios";
import socket from "../../socket/socket";
import applyBranchFilters from "./filters/applyBranchFilters";

const token = localStorage.getItem("token");

export default function useBranches(filter, selectedBranch, setSelectedBranch) {
  const [branchesRaw, setBranchesRaw] = useState([]); // original
  const [branches, setBranches] = useState([]); // filtered
  const [counts, setCounts] = useState({
    activeBranch: 0,
    inactiveBranch: 0,
  });

  /** ---------------- APPLY FILTERING ---------------- **/
  const applyFilters = (source) => {
    const { finalList, counts } = applyBranchFilters(source, {
      activeBranch: filter.activeBranch,
      inactiveBranch: filter.inactiveBranch,
      branch: filter.branch,
    });

    setBranches(finalList);
    setCounts(counts);
  };

  /** ---------------- FETCH ALL ---------------- **/
  const fetchBranches = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}track/branches`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.code === 1) {
        setBranchesRaw(res.data.data || []);
      }
    } catch (err) {
      console.error("Fetch Branches Error:", err);
    }
  };

  /** ---------------- FETCH SINGLE ---------------- **/
  const fetchBranch = async (id) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}track/branch/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.code !== 1) return;

      setBranchesRaw((prev) => [
        ...prev.filter((b) => b._id !== id),
        res.data.data,
      ]);
    } catch (err) {
      console.error("Single Branch Fetch Error:", err);
    }
  };

  const updateSelectedBranch = async (branchId) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}track/branch/${branchId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data?.data) {
        const freshBranch = res.data.data;

        // Update popup immediately
        setSelectedBranch(freshBranch);

        // Update stored branch list
        setBranchesRaw((prev) => [
          ...prev.filter((b) => b._id !== branchId),
          freshBranch,
        ]);
      }
    } catch (err) {
      console.warn("Failed to refresh branch, using cached data.");
      const fallback = branches.find((b) => b._id === branchId);
      setSelectedBranch(fallback || null);
    }
  };

  /** ---------------- SOCKET ORDER UPDATE ---------------- **/
  useEffect(() => {
    const handleOrderUpdate = (data) => {
      if (data?.branchId) fetchBranch(data.branchId);
    };

    socket.on("admin:ordersUpdate", handleOrderUpdate);
    return () => socket.off("admin:ordersUpdate", handleOrderUpdate);
  }, []);

  /** ---------------- REAPPLY FILTER WHEN RAW CHANGES ---------------- **/
  useEffect(() => {
    applyFilters(branchesRaw);
  }, [branchesRaw]);

  /** ---------------- REAPPLY FILTER WHEN FILTER SETTINGS CHANGE ---------------- **/
  useEffect(() => {
    applyFilters(branchesRaw);
  }, [filter.activeBranch, filter.inactiveBranch, filter.branch]);

  /** ---------------- AUTO-SYNC POPUP ---------------- **/
  useEffect(() => {
    if (!selectedBranch) return;

    const updated = branches.find((b) => b._id === selectedBranch._id);

    if (!updated || updated.hide) {
      setSelectedBranch(null); // close popup if hidden or removed
    } else {
      setSelectedBranch(updated);
    }
  }, [branches]);

  /** ---------------- INITIAL LOAD ---------------- **/
  useEffect(() => {
    fetchBranches();
  }, []);

  return { branches, counts, updateSelectedBranch };
}
